import type { SessionView, SourceDto } from "@nabla/api";
import { designCopy } from "../design/copy.ts";
import { localDate, saoPaulo } from "../design/format/dates.ts";
import { artigos } from "../design/format/text.ts";
import { wordsCopy as t } from "./copy.ts";

interface Named {
  readonly nome: string;
  readonly genero: "f" | "m";
  readonly a: string;
  readonly A: string;
  readonly da: string;
  readonly na: string;
}

export interface Words {
  readonly brand: string;
  readonly brandGenero: "f" | "m";
  readonly fonte: Named | null;
  readonly resultado: { readonly um: string; readonly varios: string; readonly genero: "f" | "m" };
  readonly vendas: string;
  readonly salesSource: SessionView["derived"]["salesSource"];
  readonly accounts: number;
  readonly unknownTax: ReadonlySet<string>;
  readonly criativos: boolean;
  readonly conversas: boolean;
  readonly goals: boolean;
  readonly today: string;
}

function named(nome: string, genero: "f" | "m"): Named {
  const art = artigos[genero];
  return { nome, genero, a: `${art.a} ${nome}`, A: `${art.A} ${nome}`, da: `${art.da} ${nome}`, na: `${art.na} ${nome}` };
}

export const metaNamed = named(t.meta, "m");

const kinds = designCopy.fonte;
const defaults = {
  loja: { fonte: named(kinds.shopify, "f"), resultado: { um: "pedido", varios: "pedidos", genero: "m" }, vendas: "vendas" },
  varias: { fonte: named(kinds.shopify, "f"), resultado: { um: "pedido", varios: "pedidos", genero: "m" }, vendas: "vendas" },
  asaas: { fonte: named(kinds.asaas, "m"), resultado: { um: "venda", varios: "vendas", genero: "f" }, vendas: "pagamentos confirmados" },
  nenhuma: { fonte: null, resultado: { um: "pedido", varios: "pedidos", genero: "m" }, vendas: "vendas" },
} as const;

function genderOf(noun: string): "f" | "m" {
  return /a$/.test(noun) ? "f" : "m";
}

export function wordsOf(session: Pick<SessionView, "ui" | "derived">, now: number): Words {
  const { ui, derived } = session;
  const base = defaults[derived.salesSource];
  const labels = ui.labels;
  const fonte = base.fonte === null ? null : labels.fonteVendas ? named(labels.fonteVendas.nome, labels.fonteVendas.genero) : base.fonte;
  const resultado = labels.resultado ? { ...labels.resultado, genero: genderOf(labels.resultado.um) } : base.resultado;
  return {
    brand: ui.brand.name,
    brandGenero: ui.brand.genero,
    fonte,
    resultado,
    vendas: labels.vendas ?? base.vendas,
    salesSource: derived.salesSource,
    accounts: derived.accounts.length,
    unknownTax: new Set(derived.accounts.flatMap((account) => (account.tax === "desconhecido" ? [account.ref.id] : []))),
    criativos: derived.modules.criativos,
    conversas: derived.modules.conversas,
    goals: derived.goals,
    today: todayOf(now),
  };
}

export function sourceNamed(source: SourceDto, words: Words): Named {
  if (source.kind === "meta") return metaNamed;
  if (source.kind === "vendas") return words.salesSource === "varias" ? named(source.label, "f")
    : (words.fonte ?? named(source.label, "f"));
  return words.fonte ?? metaNamed;
}

export function kindNamed(kind: "meta" | "shopify" | "asaas", words: Words): Named {
  if (kind === "meta") return metaNamed;
  if (words.fonte !== null && words.salesSource !== "varias") return words.fonte;
  return kind === "asaas" ? defaults.asaas.fonte : defaults.loja.fonte;
}

export function todayOf(now: number): string {
  return localDate(new Date(now).toISOString(), saoPaulo);
}
